import React, { useState, useEffect } from "react";
import { Button, Flex, message, Modal, Typography, Tabs, Select, Table, Tag, Tooltip } from "antd";
import MobileViewAcceptRejectList from "./MobileViewAcceptRejectList";
import AcceptRejectOrdersTable from "./AcceptRejectOrdersTable";
import AcceptRejectOrdersService from "../../services/acceptRejectOrdersService";
import RejectOrderModal from "./RejectOrderModal";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import moment from "moment";
const { TabPane } = Tabs;

const REJECTION_REASON_LABELS = {
  deadline_required_until: "Deadline Required Until",
  low_budget: "Low Budget",
  not_my_expertise: "Not My Expertise",
  already_busy: "Already Busy",
};

const rejectedColumns = [
  {
    title: "Order ID",
    dataIndex: "key",
    key: "key",
    render: (text) => (
      <Link target="_blank" to={`/order-management/my-orders/${text}`}>
        {text}
      </Link>
    ),
  },
  {
    title: "Title",
    dataIndex: "title",
    key: "title",
    render: (text) =>
      text && text.length > 30 ? (
        <Tooltip title={text}>{text.substring(0, 30)}...</Tooltip>
      ) : (
        text || "-"
      ),
  },
  {
    title: "Word Count",
    dataIndex: "word_count",
    key: "word_count",
    render: (val) => val || "-",
  },
  {
    title: "Deadline",
    dataIndex: "deadline_hard",
    key: "deadline_hard",
    render: (val) =>
      val ? moment(val).local().format("DD-MM-YYYY hh:mm A") : "-",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (val) => <Tag color="orange">{val || "-"}</Tag>,
  },
  {
    title: "Rejection Reason",
    dataIndex: "rejected_reason",
    key: "rejected_reason",
    render: (val) => {
      if (!val) return "-";
      const base = val.split(" - ")[0];
      const extra = val.includes(" - ") ? val.split(" - ").slice(1).join(" - ") : null;
      const label = REJECTION_REASON_LABELS[base] || base;
      return (
        <span>
          <Tag color="red">{label}</Tag>
          {extra && <small style={{ color: "#6B7280" }}>{extra}</small>}
        </span>
      );
    },
  },
  {
    title: "Rejected On",
    dataIndex: "rejected_on",
    key: "rejected_on",
    render: (val) =>
      val ? moment(val).local().format("DD-MM-YYYY hh:mm A") : "-",
  },
  {
    title: "Rejected By",
    key: "rejected_by",
    render: (_, record) =>
      record.rejected_by_name ? (
        <Tooltip title={record.rejected_by_email}>
          <span>{record.rejected_by_name}</span>
        </Tooltip>
      ) : (
        "-"
      ),
  },
];

const AcceptRejectOrdersMain = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const [mainTab, setMainTab] = useState("pending");

  const [payoutList, setPayoutList] = useState([]);
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmAcceptModal, setConfirmAcceptModal] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [acceptingLoader, setAcceptingLoader] = useState(false);

  const [rejectedList, setRejectedList] = useState([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedPagination, setRejectedPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const tabConfig = [
    { key: "all", label: "All", color: "red", bgColor: "#9CA3AF" },
    { key: "red", label: "Deadline Missed", color: "red", bgColor: "#FFCCCB" },
    { key: "orange", label: "Deadline > 1 Day", color: "orange", bgColor: "#FFE5B4" },
    { key: "yellow", label: "Deadline in 2-4 Days", color: "#FACA15", bgColor: "#FFF9C4" },
    { key: "green", label: "Deadline > 4 Days", color: "green", bgColor: "#C8E6C9" },
  ];

  const fetchOrders = async (filterKey) => {
    setLoading(true);
    try {
      const response = await AcceptRejectOrdersService.acceptRejectOrdersList(
        urgencyFilter,
        pagination.current,
        pagination.pageSize,
        filterKey
      );
      if (response.success) {
        setPayoutList(response.data.data.results || []);
        setPagination((prev) => ({ ...prev, total: response.data.data.count || 0 }));
      } else {
        message.error("Failed to fetch orders.");
      }
    } catch (error) {
      message.error("Error fetching orders.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectedOrders = async () => {
    setRejectedLoading(true);
    try {
      const response = await AcceptRejectOrdersService.myRejectedOrders(
        rejectedPagination.current,
        rejectedPagination.pageSize
      );
      if (response.success) {
        setRejectedList(response.data.data.results || []);
        setRejectedPagination((prev) => ({ ...prev, total: response.data.data.count || 0 }));
      } else {
        message.error("Failed to fetch rejected orders.");
      }
    } catch (error) {
      message.error("Error fetching rejected orders.");
    } finally {
      setRejectedLoading(false);
    }
  };

  useEffect(() => {
    if (mainTab === "pending") {
      fetchOrders(urgencyFilter);
    }
  }, [urgencyFilter, pagination.current, pagination.pageSize, mainTab]);

  useEffect(() => {
    if (mainTab === "rejected") {
      fetchRejectedOrders();
    }
  }, [mainTab, rejectedPagination.current, rejectedPagination.pageSize]);

  const handleAcceptClick = (record) => {
    setSelectedOrder(record);
    setConfirmAcceptModal(true);
  };

  const confirmAccept = async () => {
    if (!selectedOrder) return;
    try {
      setAcceptingLoader(true);
      const response = await AcceptRejectOrdersService.acceptOrder(selectedOrder?.key);
      if (response.success) {
        message.success("Order accepted successfully!");
        setAcceptingLoader(false);
        fetchOrders();
      }
    } catch (error) {
      setAcceptingLoader(false);
      message.error("Error accepting order.");
    } finally {
      setAcceptingLoader(false);
      setRejectModalVisible(false);
      setConfirmAcceptModal(false);
      setSelectedOrder(null);
    }
  };

  const handleRejectClick = (record) => {
    setSelectedOrder(record);
    setRejectModalVisible(true);
  };

  return (
    <Flex vertical style={{ padding: 10 }}>
      <Tabs
        activeKey={mainTab}
        onChange={setMainTab}
        style={{ marginBottom: 0 }}
        type="card"
      >
        <TabPane tab="Pending Orders" key="pending">
          {!loading && (
            <Flex
              gap={isMobile ? 8 : 0}
              style={{ width: "100%", flexDirection: isMobile ? "column" : "row", marginBottom: 8 }}
              justify="space-between"
              align={isMobile ? "flex-end" : "center"}>
              <Typography.Paragraph
                style={{ fontWeight: 500, opacity: 0.6, textAlign: "left", margin: 0, padding: 0 }}>
                Showing{" "}
                {Math.min((pagination.current - 1) * pagination.pageSize + 1, pagination.total)}{" "}
                -{" "}
                {Math.min(pagination.current * pagination.pageSize, pagination.total)}{" "}
                of {pagination.total} Orders
              </Typography.Paragraph>
            </Flex>
          )}
          {isMobile ? (
            <>
              <Select
                value={urgencyFilter}
                onChange={setUrgencyFilter}
                style={{ width: "100%", marginBottom: 16 }}
                options={tabConfig.map(({ key, label }) => ({ value: key, label }))}
              />
              <MobileViewAcceptRejectList
                payoutData={payoutList}
                onAccept={handleAcceptClick}
                onReject={handleRejectClick}
              />
            </>
          ) : (
            <Tabs activeKey={urgencyFilter} onChange={setUrgencyFilter}>
              {tabConfig.map((tab) => (
                <TabPane
                  key={tab.key}
                  tab={
                    <span
                      style={{
                        color: "black",
                        padding: "8px 16px",
                        borderRadius: "8px 8px 0 0",
                        backgroundColor: tab.bgColor,
                      }}>
                      {tab.label}
                    </span>
                  }>
                  <AcceptRejectOrdersTable
                    payoutData={payoutList}
                    loading={loading}
                    pagination={pagination}
                    setPagination={setPagination}
                    onAccept={handleAcceptClick}
                    onReject={handleRejectClick}
                  />
                </TabPane>
              ))}
            </Tabs>
          )}
        </TabPane>

        <TabPane tab="My Rejected Orders" key="rejected">
          <Table
            dataSource={rejectedList}
            columns={rejectedColumns}
            loading={rejectedLoading}
            rowKey={(record, index) => `${record.key}-${index}`}
            scroll={{ x: "max-content" }}
            pagination={{
              current: rejectedPagination.current,
              pageSize: rejectedPagination.pageSize,
              total: rejectedPagination.total,
              onChange: (page, pageSize) =>
                setRejectedPagination((prev) => ({ ...prev, current: page, pageSize })),
            }}
          />
        </TabPane>
      </Tabs>

      <Modal
        title={
          <div
            className="custom-modal-header"
            style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography.Title
              level={3}
              style={{ color: "white", margin: 0, position: "relative", left: "12px" }}>
              Confirm Acceptance
            </Typography.Title>
          </div>
        }
        className="create-order"
        open={confirmAcceptModal}
        onOk={confirmAccept}
        onCancel={() => setConfirmAcceptModal(false)}
        okText="Accept"
        footer={
          <Button
            key="accept"
            style={{ background: "#22c55e", color: "white" }}
            onClick={() => confirmAccept(selectedOrder?.key)}
            loading={acceptingLoader}>
            Accept
          </Button>
        }
        cancelText="Cancel">
        <p>Are you sure you want to accept this order?</p>
      </Modal>

      <RejectOrderModal
        visible={rejectModalVisible}
        onClose={() => setRejectModalVisible(false)}
        selectedOrder={selectedOrder}
        confirmAccept={confirmAccept}
        acceptingLoader={acceptingLoader}
        fetchOrders={fetchOrders}
        setSelectedOrder={setSelectedOrder}
        setRejectModalVisible={setRejectModalVisible}
      />
    </Flex>
  );
};

export default AcceptRejectOrdersMain;
